import { Injectable } from '@angular/core';
import { Observable, of, from } from 'rxjs';
import { switchMap, tap, map, catchError } from 'rxjs/operators';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { NarrativeStateService } from './narrative-state.service';
import { NarrativeGenerationService } from './narrative-generation.service';
import { NarrativeErrorHandlerService } from './narrative-error-handler.service';
import { FireflyService } from '../../../services/firefly.service';
import { UtilityService } from '../../../services/utility.service';
import { ExpressSdkService } from '../../../services/express-sdk.service';
import {
  NarrativeScene,
  ReferenceImage,
  StyleConfiguration,
  GeneratedVideo
} from '../models/narrative.model';
import { NARRATIVE_CONSTANTS } from '../constants/narrative.constants';
import { NarrativeHelper } from '../utils/narrative-helper';
import { I18nService } from '../../../i18n/i18n.service';

// CSV結果の型定義
export interface CSVUploadResult {
  scenes: Array<{
    prompt: string;
    negativePrompt?: string;
    seed?: number;
  }>;
  fileName: string;
}

@Injectable({
  providedIn: 'root'
})
export class NarrativeWorkflowService {
  constructor(
    private fb: FormBuilder,
    private stateService: NarrativeStateService,
    private generationService: NarrativeGenerationService,
    private errorHandler: NarrativeErrorHandlerService,
    private fireflyService: FireflyService,
    private utilityService: UtilityService,
    private expressSdkService: ExpressSdkService,
    private i18n: I18nService
  ) {}

  /**
   * ワークフローを初期化
   */
  initializeWorkflow(sceneCount: number = NARRATIVE_CONSTANTS.UI.DEFAULT_SCENES): void {
    const scenes: Partial<NarrativeScene>[] = [];
    for (let i = 0; i < sceneCount; i++) {
      scenes.push({
        prompt: '',
        negativePrompt: '',
        seed: undefined
      });
    }
    this.stateService.initializeScenes(scenes);
  }

  /**
   * CSVからシーンを読み込み
   */
  loadScenesFromCSV(uploadResult: CSVUploadResult): void {
    // 現在のデフォルト動画設定を取得
    const defaultVideoSettings = this.stateService.currentState.styleConfig.videoSettings;
    
    const scenes = uploadResult.scenes.map(sceneData => ({
      prompt: sceneData.prompt,
      negativePrompt: sceneData.negativePrompt,
      seed: sceneData.seed,
      // CSVから読み込んだシーンにもデフォルト動画設定を適用（カメラモーションのみ）
      videoSettings: defaultVideoSettings ? {
        cameraMotion: defaultVideoSettings.cameraMotion || '',
        promptStyle: '',
        shotAngle: '',
        shotSize: ''
      } : undefined
    }));

    // 最小シーン数を確保
    while (scenes.length < NARRATIVE_CONSTANTS.UI.MIN_SCENES_FOR_CSV) {
      scenes.push({
        prompt: '',
        negativePrompt: '',
        seed: undefined,
        // 追加シーンにもデフォルト動画設定を適用（カメラモーションのみ）
        videoSettings: defaultVideoSettings ? {
          cameraMotion: defaultVideoSettings.cameraMotion || '',
          promptStyle: '',
          shotAngle: '',
          shotSize: ''
        } : undefined
      });
    }

    this.stateService.initializeScenes(scenes);
    this.errorHandler.showSuccess(this.i18n.t('vnm.success.scenesLoaded', { count: String(scenes.length) }));
  }

  /**
   * スタイル画像をアップロード
   */
  uploadStyleImage(image: ReferenceImage): Observable<string> {
    return this.fireflyService.uploadFile(image.file).pipe(
      tap(response => {
        if (response?.fileId) {
          this.stateService.updateStyleConfig({
            imageFile: image,
            imageFileId: response.fileId
          });
          this.errorHandler.showSuccess(this.i18n.t('toast.styleImageUploaded'));
        }
      }),
      map(response => response?.fileId || ''),
      catchError(error => {
        this.errorHandler.handleError(error, {
          type: 'file-upload',
          operation: 'style-image-upload'
        });
        return of('');
      })
    );
  }

  /**
   * スタイル画像を削除
   */
  removeStyleImage(): void {
    this.stateService.updateStyleConfig({
      imageFile: undefined,
      imageFileId: undefined
    });
  }

  /**
   * すべてのシーンの画像を生成
   */
  generateAllSceneImages(): Observable<void> {
    const state = this.stateService.currentState;
    
    if (state.scenes.length === 0) {
      this.errorHandler.handleValidationError(this.i18n.t('vnm.err.noScenes'));
      return of(void 0);
    }

    // プロンプトが空のシーンをチェック
    const invalidScenes = state.scenes.filter(scene => !scene.prompt.trim());
    if (invalidScenes.length > 0) {
      this.errorHandler.handleValidationError(
        this.i18n.t('vnm.err.scenePromptRequired', { scenes: invalidScenes.map((_, i) => i + 1).join(', ') })
      );
      return of(void 0);
    }

    return this.generationService.generateMultipleSceneImages(
      state.scenes,
      state.styleConfig
    );
  }

  /**
   * 特定のシーンの画像を再生成
   */
  regenerateSceneImage(sceneId: string): Observable<void> {
    const scene = this.stateService.currentState.scenes.find(s => s.id === sceneId);
    if (!scene) {
      return of(void 0);
    }

    return this.generationService.generateSceneImages(
      scene,
      this.stateService.currentState.styleConfig
    ).pipe(
      tap((images) => {
        // 画像が実際に生成された場合のみ成功メッセージを表示
        if (images && images.length > 0) {
          this.errorHandler.showSuccess(this.i18n.t('vnm.success.sceneImageGenComplete'));
        }
      }),
      map(() => void 0),
      catchError((error) => {
        this.errorHandler.handleError(error, {
          type: 'image-generation',
          sceneId: sceneId
        });
        return of(void 0);
      })
    );
  }

  /**
   * すべてのシーンから動画を生成
   */
  generateVideosFromScenes(): Observable<void> {
    if (!this.stateService.areAllScenesGenerated()) {
      this.errorHandler.handleValidationError(this.i18n.t('vnm.err.allScenesNotGenerated'));
      return of(void 0);
    }

    // 動画生成開始時に即座にローディング状態を設定
    this.stateService.setVideoGenerationState(true, 0);

    const scenes = this.stateService.currentState.scenes;
    return this.generationService.generateVideosFromScenes(scenes).pipe(
      tap(results => {
        if (results.filter(r => r.video).length >= 2) {
          // 自動的に結合を開始
          setTimeout(() => {
            this.combineVideos().subscribe();
          }, 1000);
        }
      }),
      map(() => void 0)
    );
  }

  /**
   * 特定のシーンの動画を再生成
   */
  regenerateSceneVideo(sceneId: string): Observable<void> {
    const scene = this.stateService.currentState.scenes.find(s => s.id === sceneId);
    if (!scene || scene.images.length === 0) {
      this.errorHandler.handleValidationError(this.i18n.t('vnm.err.noSceneImageSelected'));
      return of(void 0);
    }

    return this.generationService.generateVideosFromScenes([scene]).pipe(
      tap(() => {
        this.errorHandler.showSuccess(this.i18n.t('vnm.success.sceneVideoGenComplete'));
      }),
      map(() => void 0)
    );
  }

  /**
   * 動画を結合
   */
  combineVideos(): Observable<void> {
    const videoUrls = this.stateService.currentState.scenes
      .filter(scene => scene.video?.url)
      .map(scene => scene.video!.url);

    if (videoUrls.length < 2) {
      this.errorHandler.handleValidationError(this.i18n.t('vnm.err.minVideosForCombine'));
      return of(void 0);
    }

    this.stateService.setVideoCombinationState(true);
    this.stateService.setCombinedVideoUrl(undefined);
    this.errorHandler.showInfo(this.i18n.t('vnm.info.combiningVideos'));

    return this.utilityService.concatenateVideos(
      videoUrls,
      'none',
      1
    ).pipe(
      tap(response => {
        this.stateService.setVideoCombinationState(false);
        
        if (response?.success && response.data?.videoUrl) {
          this.stateService.setCombinedVideoUrl(response.data.videoUrl);
          this.errorHandler.showSuccess(this.i18n.t('vnm.success.videoCombineComplete'));
        } else {
          this.errorHandler.handleError(
            new Error(this.i18n.t('vnm.err.videoCombinateFailed')),
            { type: 'combination' }
          );
        }
      }),
      map(() => void 0),
      catchError(error => {
        this.stateService.setVideoCombinationState(false);
        this.errorHandler.handleError(error, { type: 'combination' });
        return of(void 0);
      })
    );
  }

  /**
   * 結合された動画を編集
   */
  editCombinedVideo(): Observable<void> {
    const combinedVideoUrl = this.stateService.currentState.combinedVideoUrl;
    if (!combinedVideoUrl) {
      this.errorHandler.handleValidationError(this.i18n.t('vnm.err.noCombinedVideo'));
      return of(void 0);
    }

    return from(NarrativeHelper.fetchVideoAsBase64(combinedVideoUrl)).pipe(
      switchMap(videoBase64 => 
        from(this.expressSdkService.openExpressEditorForVideo(videoBase64))
      ),
      tap(editedVideoUrl => {
        if (editedVideoUrl) {
          this.stateService.setCombinedVideoUrl(editedVideoUrl);
          this.errorHandler.showSuccess(this.i18n.t('vnm.success.videoEditComplete'));
        }
      }),
      map(() => void 0),
      catchError(error => {
        this.errorHandler.handleError(error, {
          type: 'video-generation',
          operation: 'edit-combined-video'
        });
        return of(void 0);
      })
    );
  }

  /**
   * フォームグループを作成（互換性のため）
   */
  createFormGroup(): FormGroup {
    return this.fb.group({
      styleReference: [''],
      scenes: this.fb.array([])
    });
  }

  /**
   * シーンフォームを追加（互換性のため）
   */
  addSceneForm(scenesArray: FormArray): void {
    const sceneForm = this.fb.group({
      prompt: ['', Validators.required],
      negative_prompt: [''],
      seed: ['']
    });
    scenesArray.push(sceneForm);
  }
}